
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QuoteListWidget from '../../components/QuoteListWidget';
import { useQuoteList } from '../../hooks/useQuoteList';

export default function HomePage() {
  const navigate = useNavigate();

  // Original state and handlers
  const [showCallConfirm, setShowCallConfirm] = useState(false);
  const [showCallPopup, setShowCallPopup] = useState(false);
  const [showSignupPopup, setShowSignupPopup] = useState(false);

  // New signup state (replaces original formData, isSubmitting, etc.)
  const [signupFormData, setSignupFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ name: '', email: '' });

  const handlePhoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCallConfirm(true);
  };

  const confirmCall = () => {
    setShowCallConfirm(false);
    window.location.href = 'tel:+19177577737';
  };

  const handleCallClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCallPopup(true);
  };

  const handleCall = () => {
    window.location.href = 'tel:917-757-7737';
    setShowCallPopup(false);
  };

  // Show signup popup after 1.5 seconds if not seen
  useEffect(() => {
    const popupSeen = localStorage.getItem('discount_popup_seen');

    if (!popupSeen) {
      const timer = setTimeout(() => {
        setShowSignupPopup(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, []);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = { name: '', email: '' };
    let isValid = true;

    if (!signupFormData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }

    if (!signupFormData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(signupFormData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('name', signupFormData.name);
      formData.append('email', signupFormData.email);
      formData.append('offer', '10% off next order (up to 5 mattresses)');
      formData.append('source', 'Homepage Popup');
      
      // Owner notification email data
      formData.append('owner_notification_email', 'joevgindi@gmail.com');
      formData.append('owner_notification_subject', `New 10% Discount Signup: ${signupFormData.name}`);
      formData.append('owner_notification_message', `${signupFormData.name} has collected his 10% discount. ${signupFormData.email}`);
      
      // Customer autoresponder email data
      formData.append('customer_email', signupFormData.email);
      formData.append('customer_from_name', 'Midnight Mattresses');
      formData.append('customer_from_email', 'joevgindi@gmail.com');
      formData.append('customer_reply_to', 'joevgindi@gmail.com');
      formData.append('customer_subject', 'Your 10% Discount — Welcome to Midnight Mattresses');
      formData.append('customer_message', `Hi ${signupFormData.name},

<strong>Congrats on your 10% discount!</strong> Enjoy browsing our premium brand-name mattresses on our website.

<strong>Your offer:</strong> 10% off your next order (up to 5 mattresses).
<strong>To redeem:</strong> reply to this email with your selection or mention <code>10OFF</code> when ordering.

Need help deciding? Call or email us anytime.

— <strong>Midnight Mattresses</strong>
joevgindi@gmail.com • 917-757-7737`);

      const response = await fetch('https://readdy.ai/api/form/d46g1qahchb23d4efa00', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setShowThankYou(true);
        localStorage.setItem('discount_popup_seen', '1');
        // Remove auto-close, let user manually close
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setSubmitMessage('There was an error submitting your information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSignupPopup = () => {
    setShowSignupPopup(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-800" style={{ color: '#222' }}>
      {/* Call Confirmation Modal */}
      {showCallConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Make a Call?
              </h3>
              <p className="text-gray-600 mb-6">
                Would you like to make a call to Midnight Mattresses?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCallConfirm(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmCall}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Call Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quote List Widget */}
      <QuoteListWidget />

      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/a0f757b25fe373fa4ee18ed861087727/8c01a07ddce65998266bf96cfef05a86.png"
                alt="Midnight Mattresses Logo"
                className="w-24 h-24 mr-6 object-contain"
              />
              <h1 className="text-3xl font-bold text-slate-800">
                Midnight Mattresses
              </h1>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 text-slate-600">
                <i className="ri-mail-line text-lg text-cyan-600"></i>
                <span className="text-sm font-medium">joevgindi@gmail.com</span>
              </div>
              <div className="flex items-center space-x-2 text-slate-600">
                <i className="ri-phone-line text-lg text-cyan-600"></i>
                <button
                  onClick={handlePhoneClick}
                  className="text-sm font-medium hover:text-cyan-600 transition-colors cursor-pointer"
                >
                  917-757-7737
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative py-24 text-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url("https://public.readdy.ai/ai/img_res/1f13e69bcd1bc215833a3e4de5a2f237.jpg")'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-6xl font-bold text-white mb-6">
            Premium Mattresses
          </h2>
          <p className="text-2xl text-cyan-100 mb-8 max-w-3xl mx-auto">
            Experience the perfect night's sleep with our curated selection of luxury mattresses from top brands.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handlePhoneClick}
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Call Now: 917-757-7737
            </button>
            <a 
              href="mailto:joevgindi@gmail.com"
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Email Us Today
            </a>
          </div>
        </div>
      </section>

      {/* Brand Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-5xl font-bold text-center text-gray-900 mb-16">
            Premium Brands Available
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {[
              {
                name: 'Stearns & Foster',
                slug: 'stearns-foster',
                description: 'Luxury craftsmanship with premium materials and traditional construction techniques for the ultimate sleep experience.',
                image: 'https://readdy.ai/api/search-image?query=Stearns%20Foster%20luxury%20mattress%20brand%20showcase%20premium%20quality%20construction%20elegant%20design%20traditional%20craftsmanship%20white%20background%20professional%20product%20photography&width=600&height=400&seq=sf_brand_new&orientation=landscape'
              },
              {
                name: 'Serta',
                slug: 'serta',
                description: 'Innovative sleep technology with cooling comfort and motion isolation for undisturbed rest.',
                image: 'https://readdy.ai/api/search-image?query=Serta%20mattress%20brand%20showcase%20innovative%20sleep%20technology%20cooling%20comfort%20gel%20memory%20foam%20white%20background%20professional%20product%20photography&width=600&height=400&seq=serta_brand_new&orientation=landscape'
              },
              {
                name: 'Purple',
                slug: 'purple',
                description: 'Revolutionary gel grid technology for temperature regulation and pressure relief like no other.',
                image: 'https://readdy.ai/api/search-image?query=Purple%20mattress%20brand%20showcase%20revolutionary%20gel%20grid%20technology%20temperature%20regulation%20pressure%20relief%20innovative%20design%20white%20background%20professional%20product%20photography&width=600&height=400&seq=purple_brand_new&orientation=landscape'
              },
              {
                name: 'Beautyrest',
                slug: 'beautyrest',
                description: 'Advanced pocketed coil systems for superior motion isolation and personalized support.',
                image: 'https://readdy.ai/api/search-image?query=Beautyrest%20mattress%20brand%20showcase%20advanced%20pocketed%20coil%20systems%20motion%20isolation%20superior%20support%20luxury%20comfort%20white%20background%20professional%20product%20photography&width=600&height=400&seq=beauty_brand_new&orientation=landscape'
              },
              {
                name: 'Sealy',
                slug: 'sealy',
                description: 'Posturepedic technology for targeted support and optimal spinal alignment throughout the night.',
                image: 'https://readdy.ai/api/search-image?query=Sealy%20mattress%20brand%20showcase%20Posturepedic%20technology%20targeted%20support%20spinal%20alignment%20quality%20construction%20white%20background%20professional%20product%20photography&width=600&height=400&seq=sealy_brand_new&orientation=landscape'
              }
            ].map((brand) => (
              <article key={brand.slug} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border border-gray-100">
                <figure className="aspect-w-3 aspect-h-2">
                  <img 
                    src={brand.image}
                    alt={`${brand.name} mattresses`}
                    className="w-full h-48 object-cover object-center"
                  />
                </figure>
                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">
                    {brand.name}
                  </h4>
                  <p className="text-gray-700 mb-6 leading-relaxed text-sm">
                    {brand.description}
                  </p>
                  <button
                    onClick={() => navigate(`/brand/${brand.slug}`)}
                    className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-4 py-3 rounded-lg font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    View Collection
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-5xl font-bold text-white mb-8">
            Ready to Find Your Perfect Mattress?
          </h3>
          <p className="text-xl text-cyan-100 mb-12 max-w-3xl mx-auto">
            Contact Joe V Gindi, your sleep expert, for personalized recommendations and competitive pricing.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a 
              href="mailto:joevgindi@gmail.com"
              className="bg-white text-cyan-600 hover:bg-gray-100 px-10 py-5 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              📧 Email: joevgindi@gmail.com
            </a>
            <button 
              onClick={handlePhoneClick}
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-10 py-5 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              📞 Call: 917-757-7737
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900/90 backdrop-blur-sm text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://static.readdy.ai/image/a0f757b25fe373fa4ee18ed861087727/8c01a07ddce65998266bf96cfef05a86.png"
                  alt="Midnight Mattresses Logo"
                  className="w-12 h-12 mr-3 object-contain"
                />
                <h4 className="text-xl font-bold text-cyan-400">
                  Midnight Mattresses
                </h4>
              </div>
              <p className="text-slate-300">
                Premium mattresses with personal service throughout New Jersey and New York.
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4 text-cyan-400">Contact Information</h5>
              <div className="space-y-2 text-slate-300">
                <div className="flex items-center space-x-2">
                  <i className="ri-mail-line text-cyan-400"></i>
                  <span>joevgindi@gmail.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-phone-line text-cyan-400"></i>
                  <span>917-757-7737</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-map-pin-line text-cyan-400"></i>
                  <span>New Jersey / New York</span>
                </div>
              </div>
            </div>
            <div>
              <h5 className="font-semibold mb-4 text-cyan-400">Owners</h5>
              <div className="text-slate-300 space-y-1">
                <p>Joe V Gindi</p>
                <p>Leor Edri</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2024 Midnight Mattresses LLC. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-cyan-400 hover:text-cyan-300 ml-1 cursor-pointer">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Signup Popup */}
      {showSignupPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 relative">
            {/* Close Button */}
            <button
              onClick={closeSignupPopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center cursor-pointer"
            >
              ×
            </button>

            {!showThankYou ? (
              <div className="p-8">
                {/* Logo */}
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                    Midnight Mattresses
                  </h2>
                </div>

                {/* Headline */}
                <h3 className="text-2xl font-bold text-center text-gray-800 mb-2">
                  Get 10% Off Your Next Order!
                </h3>
                <p className="text-center text-gray-600 mb-6">
                  Enter your details below to receive 10% off your next order (up to 5 mattresses)
                </p>

                {/* Form */}
                <form 
                  id="discount-popup-form"
                  data-readdy-form 
                  onSubmit={handleSignupSubmit}
                  className="space-y-4"
                >
                  <div>
                    <input
                      id="dp-name"
                      type="text"
                      name="name"
                      placeholder="Your Name *"
                      value={signupFormData.name}
                      onChange={(e) => setSignupFormData({ ...signupFormData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                    />
                    {fieldErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <input
                      id="dp-email"
                      type="email"
                      name="email"
                      placeholder="Your Email Address *"
                      value={signupFormData.email}
                      onChange={(e) => setSignupFormData({ ...signupFormData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                    />
                    {fieldErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                    )}
                  </div>

                  {submitMessage && (
                    <p className="text-red-500 text-sm text-center">{submitMessage}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    style={{ backgroundColor: '#00BBD3' }}
                  >
                    {isSubmitting ? 'Processing...' : 'Claim My 10% Off'}
                  </button>
                </form>

                <p className="text-xs text-gray-500 text-center mt-4">
                  * Required fields. We respect your privacy and won't spam you.
                </p>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="mb-4">
                  <i className="ri-check-circle-fill text-green-500 text-5xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  Thank you! Your 10% discount code is on its way.
                </h3>
                <p className="text-gray-600 mb-6">
                  Check your email for your discount code and redemption instructions.
                </p>
                <button
                  onClick={closeSignupPopup}
                  className="px-6 py-2 text-white font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: '#00BBD3' }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
